<template>
    <Teleport to="body">
        <Transition name="ql-live-stream">
            <div class="ql-live-stream__modal" v-if="isLiveStreamOpen">
                <div class="ql-live-stream__modal-background" @click="closeStreamDialog"></div>
                <div class="ql-live-stream__modal-body bs-container">
                    <DsButton
                        type="interaction"
                        theme="dark"
                        class="ql-live-stream__modal-close"
                        :class="closeButtonPositionClass"
                        ref="closeButtonRef"
                        aria-label="Zavřít"
                        icon-left="cross"
                        @click="closeStreamDialog()"
                    />

                    <div class="ql-live-stream__stream" ref="streamContainerRef"></div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>
<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";
import { isLiveStreamOpen, useLotteryStream } from "./use-ql-live-stream";
import { useStackedModals } from "@sazka/utils/composables/use-stacked-modals";
import DsButton from "@sazka/design-system/src/button/ds-button.vue";
import { debounce } from "@sazka/utils/debounce";

const { addToStackedModals, removeFromStackedModals } = useStackedModals();
const streamContainerRef = ref<HTMLElement>(null);

const closeButtonRef = ref<InstanceType<typeof DsButton>>(null);
const closeButtonPositionOffset = 16;
const closeButtonPositionClass = reactive({
    top: true,
    left: false,
    inside: false,
});

const setCloseButtonPosition = () => {
    if (!isLiveStreamOpen.value) return;
    const streamContainerRect = streamContainerRef.value.getBoundingClientRect();
    const closeButtonRect = closeButtonRef.value.rootElement.getBoundingClientRect();

    if (closeButtonRect.height <= streamContainerRect.y - closeButtonPositionOffset) {
        if (!closeButtonPositionClass.top) {
            closeButtonPositionClass.left = closeButtonPositionClass.inside = false;
            closeButtonPositionClass.top = true;
        }
    } else if (closeButtonRect.width <= window.innerWidth - streamContainerRect.right - closeButtonPositionOffset) {
        if (!closeButtonPositionClass.left) {
            closeButtonPositionClass.top = closeButtonPositionClass.inside = false;
            closeButtonPositionClass.left = true;
        }
    } else if (!closeButtonPositionClass.inside) {
        closeButtonPositionClass.top = closeButtonPositionClass.left = false;
        closeButtonPositionClass.inside = true;
    }
};

const resizeEventHandlerDebounced = debounce(setCloseButtonPosition, 50);
onMounted(() => {
    window.addEventListener("resize", resizeEventHandlerDebounced);
});

onBeforeUnmount(() => {
    window.removeEventListener("resize", resizeEventHandlerDebounced);
});

const closeStreamDialog = () => {
    isLiveStreamOpen.value = false;
};

const keyDownEventHandler = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
        closeStreamDialog();
    }
};

const stream = useLotteryStream(streamContainerRef);
watch(isLiveStreamOpen, (newIsLiveStreamOpen, oldIsLiveStreamOpen) => {
    if (newIsLiveStreamOpen && !oldIsLiveStreamOpen) {
        addToStackedModals();
        window.addEventListener("keydown", keyDownEventHandler);
        stream.start();

        nextTick(() => {
            setCloseButtonPosition();
        });
    } else if (!newIsLiveStreamOpen && oldIsLiveStreamOpen) {
        removeFromStackedModals();
        window.removeEventListener("keydown", keyDownEventHandler);
        stream.stop();
    }
});
</script>
